import { getAddress, id, Interface, ZeroAddress, type JsonRpcProvider } from 'ethers'
import {
  getAllOwnedSafes as getGatewayAllOwnedSafes,
  getOwnedSafes as getGatewayOwnedSafes,
  getSafeInfo as getGatewaySafeInfo,
  ImplementationVersionState,
  TokenType,
  type AddressEx,
  type AllOwnedSafes,
  type ChainInfo,
  type OwnedSafes,
  type SafeBalanceResponse,
  type SafeInfo,
} from '@safe-global/safe-gateway-typescript-sdk'
import { EMPTY_DATA, SENTINEL_ADDRESS } from '@safe-global/protocol-kit/dist/src/utils/constants'
import { Gnosis_safe_l2__factory } from '@/types/contracts'
import { createWeb3ReadOnly } from '@/hooks/wallets/web3'
import { getSafeContractDeployment } from '@/services/contracts/deployments'
import { getLatestSafeVersion } from '@/utils/chains'
import { checksumAddress, sameAddress } from '@/utils/addresses'
import { APPCHAIN_CHAIN_ID, getAppchainChainInfo } from '@/config/appchain'

// keccak256("fallback_manager.handler.address")
const FALLBACK_HANDLER_STORAGE_SLOT = '0x6c9a6c4a39284e37ed1cf53d337577d14212a4870fb976a4366c693b939918d5'
// keccak256("guard_manager.guard.address")
const GUARD_STORAGE_SLOT = '0x4a204f620c8c5ccdca3fd54d003badd85ba500436a431f0cbda4f558c93c34c8'
const PROXY_CREATION_TOPIC = id('ProxyCreation(address,address)')
const PROXY_CREATION_IFACE = new Interface(['event ProxyCreation(address proxy, address singleton)'])

const toAddressEx = (value: string): AddressEx => ({ value: checksumAddress(value) })

const addressFromStorage = (slotValue: string): string => {
  if (!slotValue || slotValue === '0x') {
    return ZeroAddress
  }

  return getAddress(`0x${slotValue.slice(-40)}`)
}

const isZeroAddress = (address: string): boolean => sameAddress(address, ZeroAddress)

const parseModulesPage = (modulesPage: unknown): string[] => {
  if (Array.isArray(modulesPage)) {
    const [modules] = modulesPage
    return Array.isArray(modules) ? modules : []
  }

  if (modulesPage && typeof modulesPage === 'object' && 'array' in modulesPage) {
    const modules = (modulesPage as { array?: string[] }).array
    return Array.isArray(modules) ? modules : []
  }

  return []
}

export const usesOnChainSafeInfo = (chain?: Pick<ChainInfo, 'chainId' | 'transactionService'>): boolean => {
  if (!chain) {
    return false
  }

  return chain.chainId === APPCHAIN_CHAIN_ID || !chain.transactionService
}

const getImplementationVersionState = (
  chain: ChainInfo,
  implementation: string,
  version: string | null,
): ImplementationVersionState => {
  if (!version) {
    return ImplementationVersionState.UNKNOWN
  }

  const deployment = getSafeContractDeployment(chain, version)
  const networkAddress = deployment?.networkAddresses[chain.chainId]
  const isOfficial =
    !!deployment &&
    (sameAddress(implementation, networkAddress) || sameAddress(implementation, deployment.defaultAddress))

  if (!isOfficial) {
    return ImplementationVersionState.UNKNOWN
  }

  return version === getLatestSafeVersion(chain)
    ? ImplementationVersionState.UP_TO_DATE
    : ImplementationVersionState.OUTDATED
}

const readImplementation = async (provider: JsonRpcProvider, address: string): Promise<string> => {
  return addressFromStorage(await provider.getStorage(address, 0))
}

const getReadProvider = (chain: ChainInfo) => {
  return createWeb3ReadOnly(chain, chain.publicRpcUri?.value) || createWeb3ReadOnly(chain)
}

const getProxyFactoryAddress = (chain: ChainInfo): string | undefined => {
  const address = chain.contractAddresses?.safeProxyFactoryAddress
  return address || undefined
}

const parseProxyCreation = (log: { topics: readonly string[]; data: string }): string | undefined => {
  try {
    const parsed = PROXY_CREATION_IFACE.parseLog({ topics: [...log.topics], data: log.data })
    const proxy = parsed?.args?.proxy
    return typeof proxy === 'string' ? checksumAddress(proxy) : undefined
  } catch {
    return undefined
  }
}

export const getOnChainOwnedSafes = async (chain: ChainInfo, ownerAddress: string): Promise<string[]> => {
  const factoryAddress = getProxyFactoryAddress(chain)
  const provider = getReadProvider(chain)
  if (!factoryAddress || !provider || !ownerAddress) {
    return []
  }

  const logs = await provider.getLogs({
    address: factoryAddress,
    fromBlock: 0,
    toBlock: 'latest',
    topics: [PROXY_CREATION_TOPIC],
  })

  const proxies = [...new Set(logs.map(parseProxyCreation).filter((address): address is string => !!address))]
  const owned = await Promise.all(
    proxies.map(async (safeAddress) => {
      try {
        const owners = await Gnosis_safe_l2__factory.connect(safeAddress, provider).getOwners()
        return owners.some((owner) => sameAddress(owner, ownerAddress)) ? safeAddress : undefined
      } catch {
        return undefined
      }
    }),
  )

  return owned.filter((address): address is string => !!address)
}

export const getOnChainSafeInfo = async (chain: ChainInfo, safeAddress: string): Promise<SafeInfo> => {
  const provider = getReadProvider(chain)
  if (!provider) {
    throw new Error('Could not connect to the network')
  }

  const code = await provider.getCode(safeAddress)
  if (!code || code === EMPTY_DATA) {
    throw new Error('Address given is not a valid Safe Account address')
  }

  const safe = Gnosis_safe_l2__factory.connect(safeAddress, provider)

  let version: string
  try {
    version = await safe.VERSION()
  } catch {
    throw new Error('Address given is not a valid Safe Account address')
  }

  const [owners, threshold, nonce, fallbackHandlerSlot, guardSlot, modulesPage, implementation] = await Promise.all([
    safe.getOwners(),
    safe.getThreshold(),
    safe.nonce(),
    provider.getStorage(safeAddress, FALLBACK_HANDLER_STORAGE_SLOT),
    provider.getStorage(safeAddress, GUARD_STORAGE_SLOT),
    safe.getModulesPaginated(SENTINEL_ADDRESS, 10).catch(() => ({ array: [] as string[] })),
    readImplementation(provider, safeAddress),
  ])

  const fallbackHandler = addressFromStorage(fallbackHandlerSlot)
  const guard = addressFromStorage(guardSlot)
  const modules = parseModulesPage(modulesPage)

  return {
    address: toAddressEx(safeAddress),
    chainId: chain.chainId,
    nonce: Number(nonce),
    threshold: Number(threshold),
    owners: owners.map(toAddressEx),
    implementation: toAddressEx(implementation),
    implementationVersionState: getImplementationVersionState(chain, implementation, version),
    modules: modules.filter((moduleAddress) => !isZeroAddress(moduleAddress)).map(toAddressEx),
    guard: isZeroAddress(guard) ? null : toAddressEx(guard),
    fallbackHandler: isZeroAddress(fallbackHandler) ? null : toAddressEx(fallbackHandler),
    version,
    collectiblesTag: null,
    txQueuedTag: null,
    txHistoryTag: null,
    messagesTag: null,
  }
}

export const getOnChainNativeBalance = async (chain: ChainInfo, safeAddress: string): Promise<SafeBalanceResponse> => {
  const provider = getReadProvider(chain)
  if (!provider) {
    throw new Error('Could not connect to the network')
  }

  const balance = await provider.getBalance(safeAddress)

  return {
    fiatTotal: '0',
    items: [
      {
        tokenInfo: {
          type: TokenType.NATIVE_TOKEN,
          address: ZeroAddress,
          ...chain.nativeCurrency,
        },
        balance: balance.toString(),
        fiatBalance: '0',
        fiatConversion: '0',
      },
    ],
  }
}

const resolveChain = (chainId: string, chain?: ChainInfo): ChainInfo | undefined => {
  if (chain) {
    return chain
  }

  if (chainId === APPCHAIN_CHAIN_ID) {
    return getAppchainChainInfo()
  }

  return undefined
}

export const fetchSafeInfo = async (chainId: string, safeAddress: string, chain?: ChainInfo): Promise<SafeInfo> => {
  const chainInfo = resolveChain(chainId, chain)

  if (chainInfo && usesOnChainSafeInfo(chainInfo)) {
    return getOnChainSafeInfo(chainInfo, safeAddress)
  }

  return getGatewaySafeInfo(chainId, safeAddress)
}

export const fetchAllOwnedSafes = async (walletAddress: string): Promise<AllOwnedSafes> => {
  let onChainOwned: string[] = []
  try {
    onChainOwned = await getOnChainOwnedSafes(getAppchainChainInfo(), walletAddress)
  } catch (error) {
    console.error('Failed to load AppChain owned Safes from RPC', error)
  }

  try {
    const gatewayOwned = await getGatewayAllOwnedSafes(walletAddress)
    return { ...gatewayOwned, [APPCHAIN_CHAIN_ID]: onChainOwned }
  } catch {
    return { [APPCHAIN_CHAIN_ID]: onChainOwned }
  }
}

export const fetchOwnedSafes = async (
  chainId: string,
  walletAddress: string,
  chain?: ChainInfo,
): Promise<OwnedSafes> => {
  const chainInfo = resolveChain(chainId, chain)

  if (chainInfo && usesOnChainSafeInfo(chainInfo)) {
    return { safes: await getOnChainOwnedSafes(chainInfo, walletAddress) }
  }

  return getGatewayOwnedSafes(chainId, walletAddress)
}
